/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { presentUnexpectedFailure } from '../../shared/sentry/presentUnexpectedFailure';
import {
  Platform,
  Pressable,
  RefreshControl,
  ScrollView,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useFocusEffect, useIsFocused, useNavigation } from '@react-navigation/native';
import type { NavigationProp, ParamListBase } from '@react-navigation/native';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { useModulePermission } from '../../shell/session/hooks/useModulePermission';
import { useHasBusinessCapability } from '../../shell/session/hooks/useEffectiveBusinessCapabilities';
import { useCapabilityGate } from '../../shell/session/hooks/useCapabilityGate';
import { navigateInShell } from '../../shell/navigation/rootNavigation';
import {
  getKitchenAutoPrint,
  getKitchenScreenRole,
  getStationPrinters,
  setKitchenAutoPrint,
  setKitchenScreenRole,
} from '../../shared/localPrefs';
import { useConnectivityStatus } from '../../sync/hooks/useConnectivityStatus';
import { spacing } from '../../shared/theme/spacing';
import { ticketColors } from '../../shared/theme/ticketColors';
import { ShellContentFrame } from '../../shared/layout/ShellContentFrame';
import {
  getShellMainPanelWidth,
  useTabWorkspaceLayout,
} from '../../shared/layout/useContentLayout';
import { ensureTicketFonts } from '../../shared/ticket/ensureTicketFonts';
import { PaperSheet } from '../../shared/ticket/PaperSheet';
import { TicketAlertStrip } from '../../shared/ticket/TicketAlertStrip';
import { ticketSurfaces } from '../../shared/ticket/ticketSurfaces.styles';
import { SoftPressable } from '../../shared/ui/SoftPressable';
import { playKitchenAlertSound } from '../../shared/audio/kitchenAlertSound';
import { useKitchenRealtime } from '../hooks/useKitchenRealtime';
import { markSelfKitchenReady, setKitchenScreenFocused } from '../domain/kitchenScreenFocus';
import { useKitchenTicketCount } from '../providers/KitchenTicketCountProvider';
import { kitchenQueueStyles as styles } from './KitchenQueueScreen.styles';
import {
  KitchenStationsAdminPanel,
  initialStationsAdminPanelState,
  type StationsAdminPanelState,
} from './KitchenStationsAdminPanel';
import { KitchenPrintJobsPanel } from './KitchenPrintJobsPanel';
import { printKitchenComandaSafe } from '../../printing/application/printComandaDocuments';
import { useKitchenTicketSync } from '../hooks/useKitchenTicketSync';
import type {
  DiningTable,
  KitchenPrintQueueHealth,
  KitchenTicket,
  KitchenTicketLine,
  KitchenTicketStatus,
  ProductStation,
  Station,
} from '../domain/hospitalityTypes';
import {
  displayKitchenLine,
  groupKitchenTicketLines,
  filterKitchenTickets,
  filterKitchenTicketsForRole,
  isKitchenRecallable,
  kitchenActionLabel,
  kitchenRecallTargetStatus,
  kitchenTicketPrimaryRef,
  kitchenTicketPublicNotes,
  kitchenTicketSource,
  kitchenTicketSourceLabel,
  kitchenFulfillmentBadgeLabel,
  kitchenTicketDayLabel,
  kitchenTicketLineProgress,
  kitchenWaitLabel,
  kitchenStatusPhaseLabel,
  kitchenSlaCountdownLabel,
  planKitchenPrintJobs,
  summarizeKitchenProductionGroups,
  ticketAgeTone,
  type KitchenProductionStationGroup,
  type KitchenScreenRole,
  type KitchenStatusFilter,
  type KitchenTicketSource,
} from '../domain/kitchenTicketPresentation';
import {
  bumpKitchenTicket,
  getKitchenDefaultPrepMinutes,
  getKitchenStationPrinters,
  markProductUnavailable,
  setKitchenTicketHold,
  type HospitalitySnapshot,
} from '../infra/hospitalityRepo';
import { ModuleEmptyState } from '../../shared/ui/ModuleEmptyState';
import { VueltiDurmiendo } from '../../shared/ui/VueltiDurmiendo';
import { showError, showInfo, showSuccess } from '../../shared/ui/appToast';
import { useOperationalConfirm } from '../../shared/ui/useOperationalConfirm';
import { useScreenLoadShell } from '../../shared/ui/hooks-screen/useScreenLoad';

const STATUS_LABELS: Record<KitchenTicketStatus, string> = {
  pending: 'Pendiente',
  preparing: 'Preparando',
  ready: 'Listo',
  served: 'Servido',
  cancelled: 'Cancelado',
};

const NEXT_STATUS: Partial<Record<KitchenTicketStatus, KitchenTicketStatus>> = {
  pending: 'preparing',
  preparing: 'ready',
  ready: 'served',
};

/**
 * Como KDS competencia: Cola = prep; Listo = expo; Recientes = recall (30 min).
 */
const FILTERS: Array<{ id: KitchenStatusFilter; label: string }> = [
  { id: 'queue', label: 'Cola' },
  { id: 'pending', label: 'Nuevos' },
  { id: 'preparing', label: 'Preparando' },
  { id: 'ready', label: 'Listo' },
  { id: 'recent', label: 'Recientes' },
  { id: 'unpaid', label: 'Sin cobrar' },
];

/**
 * Filtro "Sin estación": comandas con ≥1 línea cuyo producto no está asignado
 * a ninguna estación existente. Centinela local — nunca se envía al backend
 * (al imprimir se traduce a null → impresora por defecto de la sucursal).
 */
const UNASSIGNED_STATION_ID = '__unassigned__';

type ActionButton = {
  label: string;
  status: KitchenTicketStatus;
  tone: 'primary' | 'danger' | 'secondary';
};

type KitchenDensity = 'comfortable' | 'compact';

function actionsForStatus(
  status: KitchenTicketStatus,
  source: KitchenTicketSource,
  fulfillmentType?: string | null,
): ActionButton[] {
  const recallTarget = kitchenRecallTargetStatus(status);
  if (recallTarget) {
    return [{ label: 'Restaurar comanda', status: recallTarget, tone: 'primary' }];
  }
  const next = NEXT_STATUS[status];
  const buttons: ActionButton[] = [];
  if (next) {
    buttons.push({
      label: kitchenActionLabel(next, source, fulfillmentType),
      status: next,
      tone: 'primary',
    });
  }
  if (status !== 'cancelled' && status !== 'served') {
    buttons.push({ label: 'Cancelar', status: 'cancelled', tone: 'danger' });
  }
  return buttons;
}

export function KitchenQueueScreen() {
  const navigation = useNavigation<NavigationProp<ParamListBase>>();
  const isFocused = useIsFocused();
  const { isOnline } = useConnectivityStatus();
  const currentBranchId = useSessionStore((s) => s.currentBranchId);
  const canViewKitchen = useModulePermission('Kitchen', 'view');
  const canUpdateStatus = useModulePermission('Kitchen', 'update_status');
  const canViewCustomer = useModulePermission('Kitchen', 'view_customer');
  const canManageStations = useModulePermission('Kitchen', 'manage_stations');
  const isRestaurantFull = useHasBusinessCapability('tables');
  const kitchenGate = useCapabilityGate('kitchen_tickets', {
    title: 'Cocina no disponible',
    subtitle: 'Este rubro no tiene comandas de cocina.',
  });
  const recentWindowMinutes = isRestaurantFull ? 180 : 60;
  const { confirm, ConfirmModal } = useOperationalConfirm();
  const [tickets, setTickets] = useState<KitchenTicket[]>([]);
  const prevTicketsRef = useRef<KitchenTicket[]>([]);
  const [tables, setTables] = useState<DiningTable[]>([]);
  const [stations, setStations] = useState<Station[]>([]);
  const [productStations, setProductStationsState] = useState<ProductStation[]>([]);
  const [selectedStationId, setSelectedStationId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const { refresh: refreshSharedKitchenSnapshot } = useKitchenTicketCount();
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [printingId, setPrintingId] = useState<string | null>(null);
  const [markingProductId, setMarkingProductId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<KitchenStatusFilter>('queue');
  const [consolidatedView, setConsolidatedView] = useState(false);
  const [showSummaryRail, setShowSummaryRail] = useState(true);
  const [screenRole, setScreenRole] = useState<KitchenScreenRole>('prep');
  const [screenRoleLoading, setScreenRoleLoading] = useState(true);
  const [defaultPrepMinutes, setDefaultPrepMinutes] = useState<number | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());
  /** Toast-style Recall: last ticket archived/cancelled in this session. */
  const [lastClosedTicketId, setLastClosedTicketId] = useState<string | null>(null);
  const [autoPrint, setAutoPrint] = useState(false);
  const [autoPrintLoading, setAutoPrintLoading] = useState(true);
  /** Salud de la cola durable de impresión (null = backend sin print_queue). */
  const [printQueue, setPrintQueue] = useState<KitchenPrintQueueHealth | null>(null);
  /** Impresora asignada por estación (stationId → printerName) para el split de auto-print. */
  const [stationPrintersMap, setStationPrintersMap] = useState<Record<string, string>>({});

  const [flashTicketIds, setFlashTicketIds] = useState<Set<string>>(() => new Set());
  const [lineStatusOverrides, setLineStatusOverrides] = useState<Record<string, 'pending' | 'done'>>({});
  const currentBranchName = useSessionStore((s) => s.branches.find((b) => b.id === s.currentBranchId)?.name ?? '');
  const { width: windowWidth, layoutWide } = useTabWorkspaceLayout();
  const panelWidth = getShellMainPanelWidth(windowWidth);

  useEffect(() => {
    ensureTicketFonts();
  }, []);

  useEffect(() => {
    void (async () => {
      const [printVal, roleVal, printers] = await Promise.all([
        getKitchenAutoPrint(),
        getKitchenScreenRole(),
        getStationPrinters(),
      ]);
      setAutoPrint(printVal === '1');
      setScreenRole(roleVal === 'expo' ? 'expo' : 'prep');
      setStationPrintersMap(printers);
      setAutoPrintLoading(false);
      setScreenRoleLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (!currentBranchId) return;
    let active = true;
    void getKitchenStationPrinters(currentBranchId)
      .then((printers) => {
        if (active && Object.keys(printers).length > 0) setStationPrintersMap(printers);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [currentBranchId]);

  useEffect(() => {
    if (autoPrintLoading) return;
    void setKitchenAutoPrint(autoPrint ? '1' : '0');
  }, [autoPrint, autoPrintLoading]);

  useEffect(() => {
    if (screenRoleLoading) return;
    void setKitchenScreenRole(screenRole);
  }, [screenRole, screenRoleLoading]);

  useEffect(() => {
    setStatusFilter(screenRole === 'expo' ? 'ready' : 'queue');
  }, [screenRole]);

  // Wake lock automático mientras la pantalla de Cocina está enfocada en web.
  useEffect(() => {
    if (Platform.OS !== 'web' || !isFocused) return;
    let sentinel: any = null;
    if (typeof navigator !== 'undefined' && 'wakeLock' in navigator) {
      (navigator as any).wakeLock
        .request('screen')
        .then((s: any) => { sentinel = s; })
        .catch(() => { /* ignore */ });
    }
    return () => {
      if (sentinel?.release) {
        try { sentinel.release(); } catch { /* ignore */ }
      }
    };
  }, [isFocused]);

  useEffect(() => {
    let active = true;
    if (!currentBranchId) {
      setDefaultPrepMinutes(null);
      return () => {
        active = false;
      };
    }
    void getKitchenDefaultPrepMinutes(currentBranchId)
      .then((minutes) => {
        if (active) setDefaultPrepMinutes(minutes);
      })
      .catch(() => {
        if (active) setDefaultPrepMinutes(null);
      });
    return () => {
      active = false;
    };
  }, [currentBranchId]);

  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  const applyTicketSnapshot = useCallback((snapshot: HospitalitySnapshot) => {
    setTickets(snapshot.kitchenTickets);
    setTables(snapshot.diningTables);
    setStations(snapshot.stations);
    setProductStationsState(snapshot.productStations);
    setPrintQueue(snapshot.printQueue);
  }, []);
  const syncScopedTickets = useKitchenTicketSync({
    branchId: currentBranchId,
    recentWindowMinutes,
    onSnapshot: applyTicketSnapshot,
  });
  const syncTickets = useCallback(async () => {
    if (!currentBranchId) {
      setTickets([]);
      setTables([]);
      return;
    }
    await syncScopedTickets();
  }, [currentBranchId, syncScopedTickets]);

  useFocusEffect(
    useCallback(() => {
      setKitchenScreenFocused(true);
      return () => setKitchenScreenFocused(false);
    }, []),
  );

  useKitchenRealtime(currentBranchId, () => void syncTickets(), {
    enabled: Boolean(currentBranchId),
    alwaysOn: false,
  });

  useEffect(() => {
    if (!isFocused || !currentBranchId) return;
    const id = setInterval(() => {
      if (!loading && !updatingId) {
        void syncTickets();
      }
    }, 30_000);
    return () => clearInterval(id);
  }, [isFocused, currentBranchId, loading, updatingId, syncTickets]);

  const printTicket = async (ticket: KitchenTicket, tableName?: string, stationId?: string | null) => {
    if (!currentBranchId) return;
    setPrintingId(ticket.id);
    try {
      await printKitchenComandaSafe({
        branchId: currentBranchId,
        ticket,
        tableName,
        stationId,
        printerName: stationId ? stationPrintersMap[stationId] : undefined,
        onError: showError,
      });
    } finally {
      setPrintingId(null);
    }
  };

  useEffect(() => {
    const hadBefore = new Set(prevTicketsRef.current.map((t) => t.id));
    const newcomers = tickets.filter(
      (t) => !hadBefore.has(t.id) && t.status !== 'served' && t.status !== 'cancelled',
    );
    if (newcomers.length > 0 && prevTicketsRef.current.length > 0) {
      playKitchenAlertSound();
      setFlashTicketIds((prev) => {
        const next = new Set(prev);
        for (const t of newcomers) next.add(t.id);
        return next;
      });
      const newcomerIds = newcomers.map((t) => t.id);
      setTimeout(() => {
        setFlashTicketIds((prev) => {
          const next = new Set(prev);
          for (const id of newcomerIds) next.delete(id);
          return next;
        });
      }, 4000);
      if (autoPrint) {
        for (const ticket of newcomers) {
          const tableName = ticket.diningTableId
            ? tables.find((table) => table.id === ticket.diningTableId)?.name
            : undefined;
          // Split por estación: cada estación con impresora asignada recibe un
          // job con solo sus líneas; el resto (sin asignar o de estaciones sin
          // impresora) va agrupado a la impresora por defecto de la sucursal.
          for (const job of planKitchenPrintJobs(
            ticket,
            stations,
            productStations,
            stationPrintersMap,
          )) {
            void printTicket({ ...ticket, lines: job.lines }, tableName, job.stationId);
          }
        }
      }
    }
    prevTicketsRef.current = tickets;
  }, [tickets, autoPrint, tables, stations, productStations, stationPrintersMap]);

  const tableNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const table of tables) {
      map.set(table.id, table.name);
    }
    return map;
  }, [tables]);

  const tableStatusById = useMemo(() => {
    const map = new Map<string, string>();
    for (const table of tables) {
      map.set(table.id, table.status);
    }
    return map;
  }, [tables]);

  const productIdsByStation = useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const ps of productStations) {
      if (!map.has(ps.stationId)) map.set(ps.stationId, new Set<string>());
      map.get(ps.stationId)!.add(ps.productId);
    }
    return map;
  }, [productStations]);

  const stationNameByProductId = useMemo(() => {
    const stMap = new Map<string, string>();
    for (const st of stations) {
      stMap.set(st.id, st.name);
    }
    const prodMap = new Map<string, string>();
    for (const ps of productStations) {
      const stName = stMap.get(ps.stationId);
      if (stName && !prodMap.has(ps.productId)) {
        prodMap.set(ps.productId, stName);
      }
    }
    return prodMap;
  }, [stations, productStations]);

  const effectiveTickets = useMemo(() => {
    if (Object.keys(lineStatusOverrides).length === 0) return tickets;
    return tickets.map((ticket) => {
      if (!ticket.lines || ticket.lines.length === 0) return ticket;
      let changed = false;
      const nextLines = ticket.lines.map((line, idx) => {
        const override = lineStatusOverrides[`${ticket.id}:${idx}`];
        if (override && override !== (line.status ?? 'pending')) {
          changed = true;
          return { ...line, status: override };
        }
        return line;
      });
      return changed ? { ...ticket, lines: nextLines } : ticket;
    });
  }, [tickets, lineStatusOverrides]);

  const roleTickets = useMemo(
    () => filterKitchenTicketsForRole(effectiveTickets, screenRole),
    [effectiveTickets, screenRole],
  );

  /**
   * Productos asignados a estaciones que EXISTEN hoy. Un product_station que
   * apunte a una estación borrada cuenta como "sin estación" (igual que en
   * summarizeKitchenProductionGroups).
   */
  const assignedProductIds = useMemo(() => {
    const validStationIds = new Set(stations.map((s) => s.id));
    const set = new Set<string>();
    for (const ps of productStations) {
      if (validStationIds.has(ps.stationId)) set.add(ps.productId);
    }
    return set;
  }, [stations, productStations]);

  const visibleTickets = useMemo(() => {
    const byStatus = filterKitchenTickets(roleTickets, statusFilter);
    const filtered = selectedStationId
      ? byStatus.filter((ticket) => {
          if (selectedStationId === UNASSIGNED_STATION_ID) {
            return ticket.lines?.some(
              (line) => !line.productId || !assignedProductIds.has(line.productId),
            );
          }
          const stationProductIds = productIdsByStation.get(selectedStationId);
          if (!stationProductIds || stationProductIds.size === 0) return true;
          return ticket.lines?.some((line) => line.productId && stationProductIds.has(line.productId));
        })
      : byStatus;
    // Sort: rush first, then by createdAt ascending (oldest first = longest waiting)
    return [...filtered].sort((a, b) => {
      if (a.rush && !b.rush) return -1;
      if (!a.rush && b.rush) return 1;
      return 0;
    });
  }, [roleTickets, statusFilter, selectedStationId, productIdsByStation, assignedProductIds]);

  const productionGroups = useMemo((): KitchenProductionStationGroup[] => {
    // Siempre agrupar contra TODAS las estaciones (así "Cocina general" junta
    // solo productos realmente sin asignar, no "todo lo que no es la estación
    // filtrada") y después elegir qué bloque mostrar.
    const all = summarizeKitchenProductionGroups(roleTickets, stations, productStations);
    if (!selectedStationId) return all;
    if (selectedStationId === UNASSIGNED_STATION_ID) {
      const general = all.filter((g) => g.stationId === null);
      return general.length > 0
        ? general
        : [{ stationId: null, stationName: 'Cocina general', totalUnits: 0, items: [] }];
    }
    const picked = all.filter((g) => g.stationId === selectedStationId);
    if (picked.length > 0) return picked;
    // Estación borrada con selección viva → bloque vacío coherente.
    return [
      {
        stationId: selectedStationId,
        stationName: stations.find((s) => s.id === selectedStationId)?.name ?? 'Estación',
        totalUnits: 0,
        items: [],
      },
    ];
  }, [roleTickets, stations, productStations, selectedStationId]);

  const totalPendingUnits = useMemo(
    () => productionGroups.reduce((sum, g) => sum + g.totalUnits, 0),
    [productionGroups],
  );

  const lastClosedTicket = useMemo(() => {
    if (!lastClosedTicketId) return null;
    return tickets.find((t) => t.id === lastClosedTicketId && isKitchenRecallable(t)) ?? null;
  }, [lastClosedTicketId, tickets]);

  const hasOpenTickets = useMemo(
    () => tickets.some((t) => t.status === 'pending' || t.status === 'preparing' || t.status === 'ready'),
    [tickets],
  );

  const loadTickets = useCallback(async () => {
    if (!currentBranchId) {
      setTickets([]);
      setTables([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      await syncTickets();
    } catch (error) {
      presentUnexpectedFailure({ module: 'hospitality', op: 'kitchen_queue.load', error, userMessage: 'No se pudieron cargar las comandas.' });
    } finally {
      setLoading(false);
    }
  }, [currentBranchId, syncTickets]);

  const { refreshing, onRefresh } = useScreenLoadShell(
    'KitchenQueue',
    async (opts) => {
      if (!currentBranchId) return { silent: true };
      if (opts?.force) {
        await syncTickets();
      } else {
        await loadTickets();
      }
    },
  );

  const applyStatusChange = async (
    ticket: KitchenTicket,
    status: KitchenTicketStatus,
  ) => {
    if (!canUpdateStatus) {
      showError('Cocina', 'No tenés permiso para actualizar comandas');
      return;
    }
    setUpdatingId(ticket.id);
    try {
      const wasRecall = isKitchenRecallable(ticket);
      await bumpKitchenTicket(ticket.id, status);
      if (status === 'ready') {
        markSelfKitchenReady(ticket.id);
      }
      // Silent refresh — no full-screen loader (KDS bumps are high-frequency).
      await syncTickets();
      await refreshSharedKitchenSnapshot();
      if (status === 'served' || status === 'cancelled') {
        setLastClosedTicketId(ticket.id);
        showSuccess(
          'Cocina',
          status === 'served' ? 'Sacada de cocina' : 'Comanda cancelada',
        );
      } else if (wasRecall) {
        setLastClosedTicketId(null);
        showSuccess('Cocina', 'Comanda restaurada');
        if (statusFilter === 'recent') setStatusFilter('queue');
      } else if (status === 'ready' && statusFilter === 'queue') {
        showSuccess('Cocina', 'Pasó a Listo');
      }
    } catch (error) {
      if (!isOnline) {
        showError('Cocina', 'Sin conexión');
      } else {
        presentUnexpectedFailure({ module: 'hospitality', op: 'cocina', error: error, title: 'Cocina', userMessage: 'No se pudo actualizar la comanda' });
      }
    } finally {
      setUpdatingId(null);
    }
  };

  const handleStatusChange = (
    ticket: KitchenTicket,
    status: KitchenTicketStatus,
  ) => {
    const ref = kitchenTicketPrimaryRef(ticket);
    if (status === 'cancelled') {
      const isOnline = kitchenTicketSource(ticket) === 'online' || !!ticket.customerOrderId;
      void (async () => {
        const ok = await confirm({
          title: `Cancelar ${ref}`,
          message: isOnline
            ? 'Solo saca la comanda de cocina. Para anular el pedido online, usá Pedidos.'
            : '¿Seguro que querés cancelar esta comanda?',
          confirmLabel: 'Cancelar comanda',
          destructive: true,
          iconName: 'trash-outline',
        });
        if (ok) await applyStatusChange(ticket, status);
      })();
      return;
    }
    if (status === 'served') {
      if (ticket.paymentState === 'pending') {
        showInfo(
          'Sin cobrar',
          'Esta comanda se mandó a cocina antes de cobrar. Cobrala en el POS para entregarla.',
        );
        return;
      }
      void (async () => {
        const ok = await confirm({
          title: kitchenActionLabel('served', kitchenTicketSource(ticket), ticket.fulfillmentType),
          message: `${ref}: la sacás de la cola. Podés deshacerla desde Recientes si te equivocás.`,
          confirmLabel: 'Listo',
          destructive: false,
          iconName: 'checkmark-circle-outline',
        });
        if (ok) await applyStatusChange(ticket, status);
      })();
      return;
    }
    void applyStatusChange(ticket, status);
  };

  const recallLastClosed = () => {
    if (!lastClosedTicket) return;
    const target = kitchenRecallTargetStatus(lastClosedTicket.status);
    if (!target) return;
    void applyStatusChange(lastClosedTicket, target);
  };

  const openOnlineOrder = (orderId: string) => {
    navigateInShell(navigation, 'OnlineOrderDetail', { orderId });
  };

  const handleMarkUnavailable = async (productId: string, productName?: string) => {
    const ok = await confirm({
      title: 'Producto agotado',
      message: `¿Marcar "${productName ?? 'este producto'}" como no disponible? Se reflejará en POS y pedidos de toda la sucursal.`,
      confirmLabel: 'Marcar agotado',
      destructive: true,
      iconName: 'alert-circle-outline',
    });
    if (!ok) return;
    setMarkingProductId(productId);
    try {
      await markProductUnavailable(productId, currentBranchId ?? '');
      showInfo('No hay', 'El producto se marcó como agotado. Se reflejará en POS y pedidos.');
      await syncTickets();
    } catch (e: unknown) {
      presentUnexpectedFailure({ module: 'hospitality', op: 'no-se-pudo-marcar', error: e, title: 'No se pudo marcar', userMessage: 'Intentá de nuevo.' });
    } finally {
      setMarkingProductId(null);
    }
  };

  const handleLineToggle = async (ticket: KitchenTicket, lineIdx: number) => {
    if (!canUpdateStatus) return;
    const currentLine = ticket.lines?.[lineIdx];
    if (!currentLine) return;
    const nextLineStatus: 'pending' | 'done' =
      currentLine.status === 'done' ? 'pending' : 'done';
    const overrideKey = `${ticket.id}:${lineIdx}`;

    setLineStatusOverrides((prev) => ({
      ...prev,
      [overrideKey]: nextLineStatus,
    }));

    // Igual que en el prototipo: si la comanda estaba en Pendiente y tildás el primer ítem,
    // pasa automáticamente a Preparando (sin saltar nunca sola a Listo).
    if (nextLineStatus === 'done' && ticket.status === 'pending') {
      setTickets((prev) =>
        prev.map((t) =>
          t.id === ticket.id
            ? { ...t, status: 'preparing', statusEnteredAt: new Date().toISOString() }
            : t,
        ),
      );
      try {
        await bumpKitchenTicket(ticket.id, 'preparing');
        await syncTickets();
        await refreshSharedKitchenSnapshot();
      } catch (e: unknown) {
        presentUnexpectedFailure({
          module: 'hospitality',
          op: 'no-se-pudo-marcar',
          error: e,
          title: 'No se pudo marcar',
          userMessage: 'Intentá de nuevo.',
        });
      }
    }
  };

  const goToPos = () => {
    navigateInShell(navigation, 'POS');
  };

  const [showStationsAdmin, setShowStationsAdmin] = useState(false);
  const [showPrintJobs, setShowPrintJobs] = useState(false);
  // El header se remonta al cambiar de rama (loading/vacío/lista/tablero) cuando
  // llega el snapshot: el estado del panel vive acá para sobrevivir al remount.
  const [stationsAdminPanelState, setStationsAdminPanelState] = useState<StationsAdminPanelState>(
    initialStationsAdminPanelState
  );
  const patchStationsAdminPanelState = useCallback(
    (patch: Partial<StationsAdminPanelState>) =>
      setStationsAdminPanelState((prev) => ({ ...prev, ...patch })),
    []
  );

  const handleToggleHold = async (ticket: KitchenTicket) => {
    if (!canUpdateStatus) return;
    const newHold = !ticket.hold;
    // Optimistic update: el botón cambia a Soltar / En espera y muestra el badge al instante sin toast invasivo
    setTickets((prev) =>
      prev.map((t) => (t.id === ticket.id ? { ...t, hold: newHold } : t)),
    );
    setUpdatingId(ticket.id);
    try {
      await setKitchenTicketHold(ticket.id, newHold);
      await syncTickets();
    } catch (error) {
      presentUnexpectedFailure({ module: 'hospitality', op: 'cocina', error: error, title: 'Cocina', userMessage: 'No se pudo cambiar el hold' });
    } finally {
      setUpdatingId(null);
    }
  };

  const printJobsAlertCount = printQueue
    ? printQueue.pendingCount + printQueue.failedCount
    : 0;

  const listHeaderPropsRef = useRef({
    currentBranchName,
    screenRole,
    isOnline,
    layoutWide,
    consolidatedView,
    showSummaryRail,
    totalPendingUnits,
    roleTickets,
    statusFilter,
    canUpdateStatus,
    lastClosedTicket,
    recallLastClosed,
    stations,
    selectedStationId,
    canManageStations,
    showStationsAdmin,
    showPrintJobs,
    autoPrint,
    printQueue,
    printJobsAlertCount,
    currentBranchId,
    productStations,
    stationPrintersMap,
    stationsAdminPanelState,
  });
  listHeaderPropsRef.current = {
    currentBranchName,
    screenRole,
    isOnline,
    layoutWide,
    consolidatedView,
    showSummaryRail,
    totalPendingUnits,
    roleTickets,
    statusFilter,
    canUpdateStatus,
    lastClosedTicket,
    recallLastClosed,
    stations,
    selectedStationId,
    canManageStations,
    showStationsAdmin,
    showPrintJobs,
    autoPrint,
    printQueue,
    printJobsAlertCount,
    currentBranchId,
    productStations,
    stationPrintersMap,
    stationsAdminPanelState,
  };

  const ListHeaderComponent = useCallback(() => {
    const p = listHeaderPropsRef.current;
    return (
      <View style={styles.stack}>
        {/* 1. HEADER COMANDO UNIFICADO (Sin botón "Más": todo directo en su contexto) */}
        <View
          style={[styles.topShellCard, !p.layoutWide && styles.topShellCardMobile]}
          testID="hospitality.cocina.header"
        >
          <View style={styles.headerRow}>
            <View style={styles.titleCluster}>
              <Text style={[styles.screenTitle, !p.layoutWide && styles.screenTitleMobile]}>
                Comandas en vivo
              </Text>
              <View style={styles.livePill}>
                <View style={[styles.liveDot, !p.isOnline && styles.liveDotOffline]} />
                <Text style={styles.livePillText}>
                  {p.currentBranchName ? `${p.currentBranchName} · ` : ''}
                  {p.isOnline ? 'En línea' : 'Sin conexión'}
                </Text>
              </View>
            </View>

            <View style={styles.controlsCluster} testID="hospitality.cocina.mode-controls">
              {/* Perfil: Cocina | Entrega */}
              <View style={styles.segGroup} accessibilityRole="tablist">
                <TouchableOpacity
                  style={[styles.segBtn, p.screenRole === 'prep' && styles.segBtnActive]}
                  onPress={() => setScreenRole('prep')}
                  activeOpacity={0.75}
                  accessibilityRole="button"
                  accessibilityLabel="Perfil Cocina"
                  testID="hospitality.cocina.role.prep"
                >
                  <Text style={[styles.segBtnText, p.screenRole === 'prep' && styles.segBtnTextActive]}>
                    Cocina
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.segBtn, p.screenRole === 'expo' && styles.segBtnActive]}
                  onPress={() => setScreenRole('expo')}
                  activeOpacity={0.75}
                  accessibilityRole="button"
                  accessibilityLabel="Perfil Entrega"
                  testID="hospitality.cocina.role.expo"
                >
                  <Text style={[styles.segBtnText, p.screenRole === 'expo' && styles.segBtnTextActive]}>
                    Entrega
                  </Text>
                </TouchableOpacity>
              </View>

              {/* Vista: Por comanda | Por producto */}
              <View style={styles.segGroup} accessibilityRole="tablist">
                <TouchableOpacity
                  style={[styles.segBtn, !p.consolidatedView && styles.segBtnActive]}
                  onPress={() => setConsolidatedView(false)}
                  activeOpacity={0.75}
                  accessibilityRole="button"
                  accessibilityLabel="Por comanda"
                  testID="hospitality.cocina.view.tickets"
                >
                  <Text style={[styles.segBtnText, !p.consolidatedView && styles.segBtnTextActive]}>
                    Por comanda
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.segBtn, p.consolidatedView && styles.segBtnActive]}
                  onPress={() => setConsolidatedView(true)}
                  activeOpacity={0.75}
                  accessibilityRole="button"
                  accessibilityLabel="Por producto"
                  testID="hospitality.cocina.view.all-day"
                >
                  <Text style={[styles.segBtnText, p.consolidatedView && styles.segBtnTextActive]}>
                    Por producto
                  </Text>
                </TouchableOpacity>
              </View>

              {/* Toggle Resumen lateral (Desktop en Por comanda) */}
              {p.layoutWide && !p.consolidatedView && p.screenRole === 'prep' ? (
                <TouchableOpacity
                  style={[styles.iconActionBtn, p.showSummaryRail && styles.iconActionBtnActive]}
                  onPress={() => setShowSummaryRail((v) => !v)}
                  activeOpacity={0.75}
                  accessibilityRole="button"
                  accessibilityLabel="Resumen lateral"
                  testID="hospitality.cocina.summary-rail.toggle"
                >
                  <Ionicons
                    name="albums-outline"
                    size={15}
                    color={p.showSummaryRail ? ticketColors.brandInk : ticketColors.ink}
                  />
                  <Text style={[styles.iconActionBtnText, p.showSummaryRail && styles.iconActionBtnTextActive]}>
                    Resumen lateral
                  </Text>
                </TouchableOpacity>
              ) : null}

              {/* Botón directo de Impresora / Auto-print (reemplaza el cajón "Más") */}
              <TouchableOpacity
                style={[
                  styles.iconActionBtn,
                  (p.showPrintJobs || p.autoPrint) && styles.iconActionBtnActive,
                ]}
                onPress={() => setShowPrintJobs((v) => !v)}
                activeOpacity={0.75}
                accessibilityRole="button"
                accessibilityLabel="Impresiones y auto-impresión"
                testID="hospitality.cocina.print-jobs"
              >
                <Ionicons
                  name="print-outline"
                  size={15}
                  color={p.showPrintJobs || p.autoPrint ? ticketColors.brandInk : ticketColors.ink}
                />
                <Text
                  style={[
                    styles.iconActionBtnText,
                    (p.showPrintJobs || p.autoPrint) && styles.iconActionBtnTextActive,
                  ]}
                >
                  {p.autoPrint ? 'Auto-print' : 'Impresora'}
                </Text>
                {p.printJobsAlertCount > 0 ? (
                  <View style={styles.moreBadge}>
                    <Text style={styles.moreBadgeText}>{p.printJobsAlertCount}</Text>
                  </View>
                ) : null}
              </TouchableOpacity>
            </View>
          </View>

          {/* 2. SUB-BARRA: PÍLDORAS DE ESTADO CON CONTADOR + ESTACIONES (.kds-subbar del proto) */}
          <View style={styles.subbarRow} testID="hospitality.cocina.kpi.sheet">
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={styles.statusTabsScroll}
            >
              {FILTERS.map((f) => {
                const count = filterKitchenTickets(p.roleTickets, f.id).length;
                const active = p.statusFilter === f.id;
                const isWarn = f.id === 'queue' && count > 0;
                const isOk = f.id === 'ready' && count > 0;
                const isBad = f.id === 'unpaid' && count > 0;
                return (
                  <TouchableOpacity
                    key={f.id}
                    style={[styles.statusTab, active && styles.statusTabActive]}
                    onPress={() => setStatusFilter(f.id)}
                    activeOpacity={0.75}
                    accessibilityRole="button"
                    accessibilityLabel={`${f.label}${count > 0 ? `, ${count}` : ', ninguna'}`}
                    testID={`hospitality.cocina.filter.${f.id}`}
                  >
                    <Text style={[styles.statusTabLabel, active && styles.statusTabLabelActive]}>
                      {f.label}
                    </Text>
                    <View
                      style={[
                        styles.statusTabCountBox,
                        isWarn && styles.statusTabCountBoxWarn,
                        isOk && styles.statusTabCountBoxOk,
                        isBad && styles.statusTabCountBoxBad,
                        active && styles.statusTabCountBoxActive,
                      ]}
                    >
                      <Text
                        style={[
                          styles.statusTabCountText,
                          isWarn && styles.statusTabCountTextWarn,
                          isOk && styles.statusTabCountTextOk,
                          isBad && styles.statusTabCountTextBad,
                          active && styles.statusTabCountTextActive,
                        ]}
                      >
                        {count}
                      </Text>
                    </View>
                  </TouchableOpacity>
                );
              })}
            </ScrollView>

            {p.stations.length > 0 || p.canManageStations ? (
              <View style={styles.stationPillsRow} testID="hospitality.cocina.station-filter">
                {p.stations.length > 0 ? (
                  <>
                    <Text style={styles.stationLbl}>Estación:</Text>
                    <TouchableOpacity
                      style={[
                        styles.stationChip,
                        p.selectedStationId == null && styles.stationChipActive,
                      ]}
                      onPress={() => setSelectedStationId(null)}
                      activeOpacity={0.75}
                      accessibilityRole="button"
                      accessibilityLabel="Todas las estaciones"
                    >
                      <Text
                        style={[
                          styles.stationChipText,
                          p.selectedStationId == null && styles.stationChipTextActive,
                        ]}
                      >
                        Todas
                      </Text>
                    </TouchableOpacity>
                    {p.stations.map((s) => {
                      const stActive = p.selectedStationId === s.id;
                      return (
                        <TouchableOpacity
                          key={s.id}
                          style={[styles.stationChip, stActive && styles.stationChipActive]}
                          onPress={() => setSelectedStationId(s.id)}
                          activeOpacity={0.75}
                          accessibilityRole="button"
                          accessibilityLabel={`Estación ${s.name}`}
                        >
                          <Text
                            style={[styles.stationChipText, stActive && styles.stationChipTextActive]}
                          >
                            {s.name}
                          </Text>
                        </TouchableOpacity>
                      );
                    })}
                    {/* "Sin estación" (FUDO: Sin área asignada) — comandas con líneas sin asignar */}
                    <TouchableOpacity
                      style={[
                        styles.stationChip,
                        p.selectedStationId === UNASSIGNED_STATION_ID && styles.stationChipActive,
                      ]}
                      onPress={() => setSelectedStationId(UNASSIGNED_STATION_ID)}
                      activeOpacity={0.75}
                      accessibilityRole="button"
                      accessibilityLabel="Sin estación"
                      testID="hospitality.cocina.station-filter.unassigned"
                    >
                      <Text
                        style={[
                          styles.stationChipText,
                          p.selectedStationId === UNASSIGNED_STATION_ID &&
                            styles.stationChipTextActive,
                        ]}
                      >
                        Sin estación
                      </Text>
                    </TouchableOpacity>
                  </>
                ) : null}

                {p.canManageStations ? (
                  <TouchableOpacity
                    style={[
                      styles.stationChip,
                      p.showStationsAdmin && styles.iconActionBtnActive,
                    ]}
                    onPress={() => setShowStationsAdmin((v) => !v)}
                    activeOpacity={0.75}
                    accessibilityRole="button"
                    accessibilityLabel="Estaciones"
                    testID="hospitality.cocina.stations-admin"
                  >
                    <Ionicons
                      name="options-outline"
                      size={13}
                      color={p.showStationsAdmin ? ticketColors.brandInk : ticketColors.ink2}
                      style={{ marginRight: 4 }}
                    />
                    <Text
                      style={[
                        styles.stationChipText,
                        p.showStationsAdmin && styles.iconActionBtnTextActive,
                      ]}
                    >
                      {p.stations.length > 0 ? 'Estaciones' : '+ Estaciones'}
                    </Text>
                  </TouchableOpacity>
                ) : null}
              </View>
            ) : null}
          </View>
        </View>

        {p.printQueue && p.printQueue.stuckCount > 0 ? (
          <TicketAlertStrip
            title="Impresora sin conexión"
            subtitle={`${p.printQueue.stuckCount} comanda${p.printQueue.stuckCount === 1 ? '' : 's'} sin imprimir · Ver cola de impresión`}
            tone="bad"
            onPress={() => setShowPrintJobs(true)}
            testID="hospitality.cocina.print-queue-warning"
            accessibilityLabel={`Impresora sin conexión: ${p.printQueue.stuckCount} comandas sin imprimir`}
          />
        ) : null}

        {p.canUpdateStatus && p.lastClosedTicket ? (
          <TicketAlertStrip
            title={`Deshacer ${kitchenTicketPrimaryRef(p.lastClosedTicket)}`}
            subtitle="Restaurá la última comanda que cerraste"
            tone="warn"
            onPress={p.recallLastClosed}
            testID="hospitality.cocina.recall-chip"
            accessibilityLabel={`Deshacer ${kitchenTicketPrimaryRef(p.lastClosedTicket)}`}
          />
        ) : null}

        {p.canManageStations ? (
          <KitchenStationsAdminPanel
            visible={p.showStationsAdmin}
            onClose={() => setShowStationsAdmin(false)}
            branchId={p.currentBranchId ?? null}
            stations={p.stations}
            productStations={p.productStations}
            stationPrintersMap={p.stationPrintersMap}
            onStationPrintersMapChange={setStationPrintersMap}
            onAfterChange={syncTickets}
            onStationDeleted={(stationId) => {
              if (listHeaderPropsRef.current.selectedStationId === stationId) {
                setSelectedStationId(null);
              }
            }}
            confirm={confirm}
            panelState={p.stationsAdminPanelState}
            onPanelStateChange={patchStationsAdminPanelState}
          />
        ) : null}

        <KitchenPrintJobsPanel
          visible={p.showPrintJobs}
          onClose={() => setShowPrintJobs(false)}
          branchId={p.currentBranchId ?? null}
          autoPrint={p.autoPrint}
          onToggleAutoPrint={() => setAutoPrint((v) => !v)}
        />
      </View>
    );
  }, [confirm, syncTickets]);

  const recentCount = useMemo(
    () => filterKitchenTickets(roleTickets, 'recent').length,
    [roleTickets],
  );

  const renderEmptyGuardCard = (opts: {
    title: string;
    subtitle: string;
    testID: string;
    isFilterEmpty?: boolean;
  }) => (
    <View style={styles.emptyGuardWrap} testID={opts.testID}>
      <View style={styles.emptyGuardCard}>
        <View style={styles.vueltiStage}>
          <VueltiDurmiendo />
        </View>

        <View style={styles.livePill}>
          <View style={[styles.liveDot, !isOnline && styles.liveDotOffline]} />
          <Text style={styles.livePillText}>
            {isOnline ? 'Escuchando POS y Tienda online en vivo' : 'Sin conexión · Reintentando'}
          </Text>
        </View>

        <Text style={styles.emptyGuardTitle}>{opts.title}</Text>
        <Text style={styles.emptyGuardSub}>{opts.subtitle}</Text>

        <View style={styles.guardPulseStrip}>
          <View style={styles.guardMetric}>
            <Text style={styles.guardMetricLbl}>En cola ahora</Text>
            <Text style={styles.guardMetricVal}>
              {filterKitchenTickets(roleTickets, 'queue').length} comandas
            </Text>
          </View>
          <View style={styles.guardMetric}>
            <Text style={styles.guardMetricLbl}>Recientes del turno</Text>
            <Text style={[styles.guardMetricVal, { color: ticketColors.ok }]}>
              {recentCount} comandas
            </Text>
          </View>
          <View style={styles.guardMetric}>
            <Text style={styles.guardMetricLbl}>Impresión</Text>
            <Text
              style={[
                styles.guardMetricVal,
                printQueue && printQueue.stuckCount > 0 ? { color: ticketColors.warn } : null,
              ]}
            >
              {printQueue && printQueue.stuckCount > 0
                ? `Atascada · ${printQueue.stuckCount} pendiente${printQueue.stuckCount === 1 ? '' : 's'}`
                : autoPrint
                  ? 'Automática'
                  : 'Manual'}
            </Text>
          </View>
        </View>

        <View style={styles.emptyActionsRow}>
          <SoftPressable
            style={styles.btnPrimaryHuman}
            onPress={goToPos}
            accessibilityRole="button"
            accessibilityLabel="Abrir el POS"
          >
            <Text style={styles.btnPrimaryHumanText}>Abrir el POS</Text>
          </SoftPressable>

          {opts.isFilterEmpty && statusFilter !== 'queue' ? (
            <SoftPressable
              style={styles.btnSecondaryHuman}
              onPress={() => setStatusFilter('queue')}
              accessibilityRole="button"
              accessibilityLabel="Volver a Cola"
            >
              <Text style={styles.btnSecondaryHumanText}>Volver a Cola</Text>
            </SoftPressable>
          ) : null}
          {recentCount > 0 ? (
            <SoftPressable
              style={styles.btnSecondaryHuman}
              onPress={() => setStatusFilter('recent')}
              accessibilityRole="button"
              accessibilityLabel="Ver recientes"
            >
              <Text style={styles.btnSecondaryHumanText}>Ver recientes</Text>
            </SoftPressable>
          ) : null}
        </View>
      </View>
    </View>
  );

  const filterEmptyComponent = renderEmptyGuardCard({
    title:
      statusFilter === 'queue'
        ? 'Nada para cocinar'
        : statusFilter === 'ready'
          ? 'Nada en Listo'
          : statusFilter === 'recent'
            ? 'Sin recientes'
            : statusFilter === 'unpaid'
              ? 'Nada por cobrar'
              : 'Sin comandas acá',
    subtitle:
      statusFilter === 'queue'
        ? 'Cuando vendas en el POS o confirmes un pedido online, la comanda entra acá con alerta sonora.'
        : statusFilter === 'ready'
          ? 'Archivala cuando la retiraron o salió con el rider.'
          : statusFilter === 'recent'
            ? `Archivadas o canceladas de los últimos ${recentWindowMinutes} min. Podés restaurarlas desde acá o con Deshacer.`
            : statusFilter === 'unpaid'
              ? 'Cuando mandes una comanda antes de cobrarla, aparece acá.'
              : 'Probá otro filtro de la tira de arriba.',
    testID: 'hospitality.cocina.filter-empty',
    isFilterEmpty: true,
  });

  const renderTicketItem = (item: KitchenTicket, ticketIndex = 0) => {
    const source = kitchenTicketSource(item);
    const actions = actionsForStatus(item.status, source, item.fulfillmentType);
    const primaryAction = actions.find((a) => a.tone === 'primary') ?? null;
    const dangerAction = actions.find((a) => a.tone === 'danger') ?? null;
    const busy = updatingId === item.id;
    const tableName = item.diningTableId ? tableNameById.get(item.diningTableId) : undefined;
    const formattedTable = tableName
      ? /^mesa\b/i.test(tableName.trim())
        ? tableName.trim()
        : `Mesa ${tableName.trim()}`
      : null;
    const phaseLabel = kitchenStatusPhaseLabel(item.status, item.statusEnteredAt, item.createdAt, nowMs);
    const dayLabel = kitchenTicketDayLabel(item.createdAt, nowMs);
    const totalWait = kitchenWaitLabel(item.createdAt, nowMs);
    const ageLabel = dayLabel
      ? `${dayLabel}${totalWait ? ` · ${totalWait} en total` : ''}`
      : phaseLabel;
    const slaLabel =
      item.status === 'preparing' ? kitchenSlaCountdownLabel(item.slaDeadlineAt, nowMs) : null;
    const primaryRef = kitchenTicketPrimaryRef(item);
    const fulfillmentLabel = kitchenFulfillmentBadgeLabel(item.fulfillmentType);
    // H13: comanda abierta sobre mesa ya liberada → Cocina decide servir o cancelar.
    const tableReleased =
      Boolean(item.diningTableId) && tableStatusById.get(item.diningTableId!) === 'available';
    const destCombinedLabel =
      [
        fulfillmentLabel && formattedTable
          ? `${fulfillmentLabel} · ${formattedTable}`
          : formattedTable ?? fulfillmentLabel,
        tableReleased ? 'liberada' : null,
      ].filter(Boolean).join(' · ') || null;
    const notes = kitchenTicketPublicNotes(item.notes);
    const orderId = item.customerOrderId?.trim() || null;
    const saleId = item.saleId?.trim() || null;
    const recallable = isKitchenRecallable(item);
    /**
     * Filtro por estación a nivel LÍNEA: al elegir una estación, la comanda
     * muestra solo sus líneas de esa estación (FUDO/Toast por sector). El
     * ticket aparece si tiene ≥1 línea de la estación; el resto queda como
     * "+N ítems de otras estaciones". Estación sin productos asignados → ve
     * todo (fallback histórico, no queda pantalla vacía).
     */
    const stationLineMatch = (line: KitchenTicketLine): boolean => {
      if (!selectedStationId) return true;
      if (selectedStationId === UNASSIGNED_STATION_ID) {
        return !line.productId || !assignedProductIds.has(line.productId);
      }
      const ids = productIdsByStation.get(selectedStationId);
      if (!ids || ids.size === 0) return true;
      return Boolean(line.productId && ids.has(line.productId));
    };
    const visibleStationLines = (item.lines ?? []).filter(stationLineMatch);
    const hiddenStationLineCount = (item.lines ?? []).length - visibleStationLines.length;
    const lineProgress = kitchenTicketLineProgress(visibleStationLines);
    const openSale = (id: string) => {
      navigateInShell(navigation, 'SaleDetail', { saleId: id });
    };
    const slaMinutes = item.slaDeadlineAt
      ? Math.max(1, Math.floor((Date.parse(item.slaDeadlineAt) - Date.parse(item.createdAt ?? '')) / 60_000))
      : defaultPrepMinutes ?? 5;
    const warnMinutes = item.slaDeadlineAt ? slaMinutes : defaultPrepMinutes ?? 5;
    const dangerMinutes = item.slaDeadlineAt ? slaMinutes + 15 : defaultPrepMinutes != null ? defaultPrepMinutes + 15 : 10;
    const ageTone = ticketAgeTone(
      item.createdAt,
      nowMs,
      warnMinutes,
      dangerMinutes,
      item.statusEnteredAt,
      item.slaDeadlineAt,
    );
    const isFlashing = flashTicketIds.has(item.id);
    const isDangerTone = ageTone === 'danger' || Boolean(item.rush);
    const isWarnTone = !isDangerTone && (ageTone === 'warn' || Boolean(item.hold));
    const isReadyStatus = item.status === 'ready';
    const ageBorder =
      isFlashing
        ? ticketColors.gold
        : isDangerTone
          ? ticketColors.bad
          : isWarnTone
            ? ticketColors.warn
            : ticketColors.rule;

    return (
      <PaperSheet
        testID={`hospitality.cocina.ticket.${item.id}`}
        style={[
          styles.ticketCard,
          recallable && styles.ticketCardRecent,
          isFlashing && styles.ticketCardFlash,
          styles.ticketCardBoard,
          {
            borderColor: ageBorder,
            borderWidth: isFlashing ? 2 : 1,
            // Hold siempre señalado punteado — también bajo danger (urgente Y en espera).
            borderStyle: item.hold ? 'dashed' : 'solid',
          },
        ]}
      >
        {/* Banda superior semántica (5px — 1:1 con .ticket-status-bar del proto) */}
        <View
          style={[
            styles.ticketTopBar,
            isReadyStatus && styles.ticketTopBarReady,
            !isReadyStatus && isDangerTone && styles.ticketTopBarDanger,
            !isReadyStatus && isWarnTone && styles.ticketTopBarWarn,
          ]}
        />

        {/* Cabezal de la comanda (1:1 con .ticket-head del proto) */}
        <View
          style={[
            styles.ticketHeaderBlock,
            isReadyStatus && styles.ticketHeaderBlockReady,
            !isReadyStatus && isDangerTone && styles.ticketHeaderBlockDanger,
            !isReadyStatus && isWarnTone && styles.ticketHeaderBlockWarn,
          ]}
        >
          {/* Fila 1: #Ref + Destino primario a la izquierda | Timer pill + SLA/Estado abajo a la derecha */}
          <View style={styles.ticketHeader}>
            <View style={styles.ticketRefGroup}>
              <Text style={styles.ticketId}>{primaryRef}</Text>
              {destCombinedLabel ? (
                <View
                  style={[
                    styles.ticketDestPrimary,
                    item.fulfillmentType === 'delivery' && styles.ticketDestDelivery,
                    item.fulfillmentType === 'pickup' && styles.ticketDestPickup,
                  ]}
                >
                  <Text
                    style={[
                      styles.ticketDestPrimaryText,
                      item.fulfillmentType === 'delivery' && styles.ticketDestDeliveryText,
                      item.fulfillmentType === 'pickup' && styles.ticketDestPickupText,
                    ]}
                  >
                    {destCombinedLabel}
                  </Text>
                </View>
              ) : null}
            </View>

            <View style={styles.ticketTimerBox}>
              {(phaseLabel && !dayLabel) || (dayLabel && ageLabel) ? (
                <View
                  style={[
                    styles.timerPill,
                    isReadyStatus && styles.timerPillReady,
                    !isReadyStatus && ageTone === 'warn' && styles.timerPillWarn,
                    !isReadyStatus && ageTone === 'danger' && styles.timerPillDanger,
                  ]}
                >
                  <Text
                    style={[
                      styles.waitLabel,
                      isReadyStatus && styles.waitLabelReady,
                      !isReadyStatus && ageTone === 'warn' && styles.waitLabelWarn,
                      !isReadyStatus && ageTone === 'danger' && styles.waitLabelDanger,
                    ]}
                  >
                    {dayLabel && ageLabel ? ageLabel : phaseLabel}
                  </Text>
                </View>
              ) : null}
              <View style={styles.slaSubRow}>
                {slaLabel ? (
                  <Text
                    style={[
                      styles.slaLabel,
                      ageTone === 'danger' && styles.slaLabelLate,
                      ageTone === 'warn' && styles.slaLabelSoon,
                    ]}
                  >
                    {slaLabel}
                  </Text>
                ) : null}
                <Text style={[styles.ticketStatus, isReadyStatus && styles.ticketStatusReady]}>
                  {STATUS_LABELS[item.status]}
                </Text>
              </View>
            </View>
          </View>

          {/* Fila 2: Badges + Cliente + Link (Ver venta ↗) | Progress dots (1/2) */}
          <View style={styles.ticketMetaSubRow}>
            <View style={styles.badgeRow}>
              <View
                style={[
                  styles.sourceBadge,
                  source === 'online' && styles.sourceOnline,
                  source === 'pos' && styles.sourcePos,
                ]}
              >
                <Text style={styles.sourceBadgeLabel}>{kitchenTicketSourceLabel(source)}</Text>
              </View>
              {item.rush ? (
                <View style={styles.rushBadge}>
                  <Text style={styles.rushBadgeLabel}>URGENTE</Text>
                </View>
              ) : null}
              {item.hold ? (
                <View style={styles.holdBadge}>
                  <Text style={styles.holdBadgeLabel}>En espera</Text>
                </View>
              ) : null}
              {item.paymentState === 'pending' ? (
                <View
                  style={[styles.unpaidBadge, ageTone === 'danger' && styles.unpaidBadgeStale]}
                  testID={`kitchen-ticket-unpaid-${item.id}`}
                >
                  <Text
                    style={[
                      styles.unpaidBadgeLabel,
                      ageTone === 'danger' && styles.unpaidBadgeLabelStale,
                    ]}
                  >
                    {totalWait ? `Sin cobrar · ${totalWait}` : 'Sin cobrar'}
                  </Text>
                </View>
              ) : null}
              {item.isAllDay ? (
                <View style={styles.allDayBadge}>
                  <Text style={styles.allDayBadgeLabel}>Por producto</Text>
                </View>
              ) : null}
              {canViewCustomer && item.customerName ? (
                <Text style={styles.customerLine} numberOfLines={1}>
                  {item.customerName}
                  {item.customerPhone ? ` · ${item.customerPhone}` : ''}
                </Text>
              ) : null}
              {orderId ? (
                <Pressable onPress={() => openOnlineOrder(orderId)} hitSlop={8}>
                  <Text style={styles.miniLink}>Ver pedido ↗</Text>
                </Pressable>
              ) : saleId ? (
                <Pressable onPress={() => openSale(saleId)} hitSlop={8}>
                  <Text style={styles.miniLink}>Ver venta ↗</Text>
                </Pressable>
              ) : null}
            </View>

            {lineProgress.total > 0 ? (
              <View style={styles.progressPill}>
                <View style={styles.progressDotsRow}>
                  {visibleStationLines.map((l, dotIdx) => (
                    <View
                      key={`${item.id}-dot-${dotIdx}`}
                      style={[
                        styles.progressDot,
                        l.status === 'done' && styles.progressDotDone,
                      ]}
                    />
                  ))}
                </View>
                <Text style={styles.progressText}>
                  {lineProgress.done}/{lineProgress.total}
                </Text>
              </View>
            ) : null}
          </View>
        </View>

        {/* Nota general del pedido (.ticket-order-note del proto) */}
        {notes ? (
          <View style={styles.ticketOrderNote}>
            <Text style={styles.ticketOrderNoteTag}>Nota</Text>
            <Text style={styles.ticketNotes}>{notes}</Text>
          </View>
        ) : null}

        {/* Renglones de ítems con checkbox izquierdo (1:1 con .kds-line del proto) */}
        {(item.lines ?? []).length > 0 ? (
          <View style={styles.linesBlock}>
            {groupKitchenTicketLines(item.lines!).map((block, blockIdx) => {
              const renderLine = (
                raw: KitchenTicketLine,
                idx: number,
                opts?: { hideComboLabel?: boolean; indent?: boolean },
              ) => {
                const line = displayKitchenLine(raw);
                const isDone = line.status === 'done';
                const canBumpLine =
                  canUpdateStatus &&
                  screenRole === 'prep' &&
                  (item.status === 'pending' || item.status === 'preparing');
                const stationLabel =
                  stations.length > 0 && line.productId
                    ? stationNameByProductId.get(line.productId)
                    : undefined;

                return (
                  <View
                    key={`${item.id}-line-${idx}`}
                    style={[
                      styles.lineRow,
                      isDone && styles.lineRowDone,
                      opts?.indent && styles.lineRowComboComponent,
                    ]}
                  >
                    <Pressable
                      style={styles.lineMainTapArea}
                      disabled={!canBumpLine}
                      accessibilityRole={canBumpLine ? 'button' : undefined}
                      accessibilityLabel={canBumpLine ? (isDone ? 'Destildar ítem' : 'Hecho') : undefined}
                      testID={canBumpLine ? `hospitality.cocina.line-ready.${item.id}.${idx}` : undefined}
                      onPress={() => {
                        if (canBumpLine) {
                          void handleLineToggle(item, idx);
                        }
                      }}
                    >
                      {canBumpLine ? (
                        <View style={[styles.lineCheckBox, isDone && styles.lineCheckBoxDone]}>
                          {isDone ? (
                            <Ionicons name="checkmark" size={15} color="#FFFFFF" />
                          ) : null}
                        </View>
                      ) : null}

                      <Text style={[styles.lineQty, isDone && styles.lineQtyDone]}>{raw.qty}×</Text>

                      <View style={styles.lineBody}>
                        <View style={styles.lineNameRow}>
                          <Text style={[styles.lineName, isDone && styles.lineNameDone]}>
                            {line.name}
                          </Text>
                          {stationLabel ? (
                            <Text style={styles.lineStationBadge}>{stationLabel}</Text>
                          ) : null}
                        </View>
                        {line.comboName && !opts?.hideComboLabel ? (
                          <Text style={styles.lineComboName}>Combo: {line.comboName}</Text>
                        ) : null}
                        {line.modifiers.length > 0 ? (
                          <View style={styles.modifierRow}>
                            {line.modifiers.map((modifier, modifierIndex) => (
                              <View
                                key={`${item.id}-line-${idx}-modifier-${modifierIndex}`}
                                style={[
                                  styles.modifierChip,
                                  modifier.kind === 'extra' && styles.modifierChipExtra,
                                  modifier.kind === 'exclude' && styles.modifierChipExclude,
                                  modifier.kind === 'allergen' && styles.modifierChipAllergen,
                                ]}
                              >
                                <Text
                                  style={[
                                    styles.modifierChipLabel,
                                    modifier.kind === 'extra' && styles.modifierChipLabelExtra,
                                    modifier.kind === 'exclude' && styles.modifierChipLabelExclude,
                                    modifier.kind === 'allergen' && styles.modifierChipLabelAllergen,
                                  ]}
                                >
                                  {modifier.quantity > 1 ? `${modifier.quantity}× ` : ''}
                                  {modifier.label}
                                </Text>
                              </View>
                            ))}
                          </View>
                        ) : null}
                        {line.lineNote ? (
                          <View style={styles.kitchenNote}>
                            <Text style={styles.kitchenNoteText}>Nota: {line.lineNote}</Text>
                          </View>
                        ) : null}
                      </View>
                    </Pressable>

                    {canBumpLine && line.productId ? (
                      <SoftPressable
                        style={styles.noHayBtn}
                        disabled={markingProductId === line.productId}
                        accessibilityRole="button"
                        accessibilityLabel="No hay"
                        onPress={() =>
                          line.productId && void handleMarkUnavailable(line.productId, line.name)
                        }
                      >
                        <Text style={styles.noHayLabel}>
                          {markingProductId === line.productId ? '…' : 'No hay'}
                        </Text>
                      </SoftPressable>
                    ) : null}
                  </View>
                );
              };

              if (block.kind === 'single') {
                if (!stationLineMatch(block.raw)) return null;
                return renderLine(block.raw, block.index);
              }

              const comboItems = block.items.filter(({ raw }) => stationLineMatch(raw));
              if (comboItems.length === 0) return null;
              return (
                <View key={`${item.id}-combo-${blockIdx}`} style={styles.comboGroup}>
                  <View style={styles.comboGroupHeaderRow}>
                    <Text style={styles.comboGroupHeader}>
                      {block.comboQty}× Combo: {block.comboName}
                    </Text>
                    <Text style={styles.comboGroupTag}>Combo</Text>
                  </View>
                  {comboItems.map(({ raw, index }) =>
                    renderLine(raw, index, { hideComboLabel: true, indent: true }),
                  )}
                </View>
              );
            })}
          </View>
        ) : null}

        {hiddenStationLineCount > 0 ? (
          <Text style={styles.lineStationHiddenNote}>
            +{hiddenStationLineCount}{' '}
            {hiddenStationLineCount === 1
              ? 'ítem de otra estación'
              : 'ítems de otras estaciones'}
          </Text>
        ) : null}

        {/* Pie de acciones de la comanda (1:1 con .ticket-foot del proto: iconos a la izq + CTA ancho a la der) */}
        <View style={styles.actionsRow}>
          <SoftPressable
            style={styles.footIconBtn}
            disabled={printingId === item.id}
            accessibilityRole="button"
            accessibilityLabel="Imprimir comanda"
            onPress={() =>
              void printTicket(
                item,
                tableName,
                selectedStationId === UNASSIGNED_STATION_ID ? null : selectedStationId,
              )
            }
            testID="hospitality.cocina.print"
          >
            <Ionicons
              name="print-outline"
              size={17}
              color={printingId === item.id ? ticketColors.ink3 : ticketColors.ink2}
            />
          </SoftPressable>

          {canUpdateStatus && (item.status === 'pending' || item.status === 'preparing') ? (
            <SoftPressable
              style={[styles.footHoldBtn, item.hold && styles.footHoldBtnActive]}
              disabled={busy}
              accessibilityRole="button"
              accessibilityLabel={item.hold ? 'Soltar' : 'En espera'}
              testID={`hospitality.cocina.hold.${item.id}`}
              onPress={() => void handleToggleHold(item)}
            >
              <Ionicons
                name={item.hold ? 'play-outline' : 'pause-outline'}
                size={15}
                color={item.hold ? '#FFFFFF' : ticketColors.warn}
              />
              <Text style={[styles.footHoldBtnLabel, item.hold && styles.footHoldBtnLabelActive]}>
                {item.hold ? 'Soltar' : 'En espera'}
              </Text>
            </SoftPressable>
          ) : null}

          {canUpdateStatus && dangerAction ? (
            <SoftPressable
              style={[styles.footIconBtn, styles.footIconBtnDanger, busy && styles.actionDisabled]}
              disabled={busy}
              accessibilityRole="button"
              accessibilityLabel={dangerAction.label}
              onPress={() => handleStatusChange(item, dangerAction.status)}
              testID={`hospitality.cocina.action.${dangerAction.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <Ionicons name="close-outline" size={20} color={ticketColors.ink2} />
            </SoftPressable>
          ) : null}

          {canUpdateStatus && primaryAction ? (
            <SoftPressable
              style={[
                styles.footPrimaryCta,
                primaryAction.status === 'ready' && styles.footPrimaryCtaReady,
                primaryAction.status === 'served' && styles.footPrimaryCtaExpo,
                busy && styles.actionDisabled,
              ]}
              disabled={busy}
              accessibilityRole="button"
              accessibilityLabel={primaryAction.label}
              onPress={() => handleStatusChange(item, primaryAction.status)}
              testID={`hospitality.cocina.action.${primaryAction.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <Text style={styles.footPrimaryCtaLabel}>
                {primaryAction.status === 'ready' && lineProgress.total > 0
                  ? lineProgress.done === lineProgress.total
                    ? `${primaryAction.label} ✓`
                    : `${primaryAction.label} (${lineProgress.done}/${lineProgress.total})`
                  : primaryAction.label}
              </Text>
              {layoutWide ? (
                <View style={styles.kbdHintBox}>
                  <Text style={styles.kbdHintText}>[{ticketIndex + 1}]</Text>
                </View>
              ) : null}
            </SoftPressable>
          ) : null}
        </View>
      </PaperSheet>
    );
  };

  const showSideRail = layoutWide && !consolidatedView && screenRole === 'prep' && showSummaryRail;
  const availableBoardWidth = showSideRail ? panelWidth - 316 : panelWidth;
  const numBoardCols = availableBoardWidth >= 1040 ? 3 : layoutWide ? 2 : 1;

  const boardColumns = useMemo(() => {
    const cols: Array<Array<{ ticket: KitchenTicket; index: number }>> = Array.from(
      { length: numBoardCols },
      () => [],
    );
    visibleTickets.forEach((ticket, index) => {
      cols[index % numBoardCols]!.push({ ticket, index });
    });
    return cols;
  }, [visibleTickets, numBoardCols]);

  const renderSideRail = () => {
    const allItems = productionGroups.flatMap((g) =>
      g.items.map((it) => ({
        ...it,
        stationName: stations.length > 0 ? g.stationName : null,
      })),
    );
    return (
      <PaperSheet style={styles.sideRailCard} testID="hospitality.cocina.summary-rail">
        <View style={styles.sideRailHeader}>
          <View style={styles.sideRailTitleRow}>
            <Ionicons name="flame-outline" size={15} color={ticketColors.brandPrimary} />
            <Text style={styles.sideRailTitle}>Total a preparar ahora</Text>
          </View>
          <Text style={styles.sideRailSub}>Suma de unidades en comandas abiertas</Text>
        </View>
        <View style={styles.sideRailBody}>
          {allItems.length > 0 ? (
            allItems.map((item) => (
              <View key={`${item.stationName ?? 'all'}-${item.key}`} style={styles.batchItemRow}>
                <View style={styles.batchQtyBadge}>
                  <Text style={styles.batchQtyText}>{item.quantity}×</Text>
                </View>
                <View style={styles.batchItemContent}>
                  <Text style={styles.batchItemName} numberOfLines={1}>
                    {item.name}
                  </Text>
                  {item.optionsLabel ? (
                    <Text style={styles.batchItemOptions} numberOfLines={1}>
                      {item.optionsLabel}
                    </Text>
                  ) : null}
                  <Text style={styles.sideRailSub} numberOfLines={1}>
                    {item.stationName ? `${item.stationName} · ` : ''}
                    En {item.ticketRefs.map((r) => r.ref).join(', ')}
                  </Text>
                </View>
              </View>
            ))
          ) : (
            <Text style={styles.stationBatchEmpty}>Todo lo pendiente está marcado como hecho.</Text>
          )}
        </View>
      </PaperSheet>
    );
  };

  const listRefreshControl =
    Platform.OS === 'web' ? undefined : (
      <RefreshControl
        refreshing={refreshing}
        onRefresh={() => void onRefresh()}
        colors={[ticketColors.ink2]}
        tintColor={ticketColors.ink2}
      />
    );

  return (
    <ShellContentFrame style={ticketSurfaces.screen}>
      {ConfirmModal}
      {loading && tickets.length === 0 ? (
        <View style={{ flex: 1, padding: spacing.lg }}>
          <ListHeaderComponent />
          <ModuleEmptyState loading loadingText="Cargando comandas…" testID="hospitality.cocina.loading" />
        </View>
      ) : !hasOpenTickets && tickets.length === 0 ? (
        <ScrollView
          contentContainerStyle={styles.listContent}
          refreshControl={listRefreshControl}
          keyboardShouldPersistTaps="handled"
        >
          <ListHeaderComponent />
          {renderEmptyGuardCard({
            title: 'Todo al día en cocina',
            subtitle:
              'Cuando vendas en el POS o confirmes un pedido online, la comanda entra acá con alerta sonora.',
            testID: 'hospitality.cocina.empty',
          })}
        </ScrollView>
      ) : consolidatedView ? (
        <ScrollView
          contentContainerStyle={styles.listContent}
          refreshControl={listRefreshControl}
          keyboardShouldPersistTaps="handled"
        >
          <ListHeaderComponent />
          <View style={styles.productionSectionWrap} testID="hospitality.cocina.production-matrix">
            <View style={styles.stationMatrixGrid}>
              {productionGroups.map((group) => (
                <PaperSheet
                  key={group.stationId ?? '__general__'}
                  style={[
                    styles.stationBatchCard,
                    productionGroups.length === 1 && styles.stationBatchCardFull,
                  ]}
                >
                  <View style={styles.stationBatchHeader}>
                    <Text style={styles.stationBatchTitle}>{group.stationName}</Text>
                    <View style={styles.stationBatchCountBadge}>
                      <Text style={styles.stationBatchCountText}>{group.totalUnits} u. pendientes</Text>
                    </View>
                  </View>
                  <View style={styles.stationBatchBody}>
                    {group.items.length > 0 ? (
                      group.items.map((item) => (
                        <View key={item.key} style={styles.batchItemRow}>
                          <View style={styles.batchQtyBadge}>
                            <Text style={styles.batchQtyText}>{item.quantity}×</Text>
                          </View>
                          <View style={styles.batchItemContent}>
                            <Text style={styles.batchItemName}>{item.name}</Text>
                            {item.optionsLabel ? (
                              <Text style={styles.batchItemOptions}>{item.optionsLabel}</Text>
                            ) : null}
                            <View style={styles.batchRefsRow}>
                              {item.ticketRefs.map((refItem) => (
                                <View key={refItem.ticketId} style={styles.batchRefPill}>
                                  <Text style={styles.batchRefPillText}>
                                    {refItem.ref} ({refItem.quantity}×)
                                  </Text>
                                </View>
                              ))}
                            </View>
                          </View>
                        </View>
                      ))
                    ) : (
                      <Text style={styles.stationBatchEmpty}>Estación al día ✓</Text>
                    )}
                  </View>
                </PaperSheet>
              ))}
            </View>

            {visibleTickets.length > 0 ? (
              <View style={{ gap: 12 }}>
                <Text style={styles.linkedTicketsHeading}>
                  Comandas activas vinculadas ({visibleTickets.length})
                </Text>
                {layoutWide ? (
                  <View style={styles.boardColumns}>
                    {boardColumns.map((column, columnIndex) => (
                      <View key={`matrix-col-${columnIndex}`} style={styles.boardColumn}>
                        {column.map(({ ticket, index }) => (
                          <React.Fragment key={ticket.id}>
                            {renderTicketItem(ticket, index)}
                          </React.Fragment>
                        ))}
                      </View>
                    ))}
                  </View>
                ) : (
                  <View style={{ gap: 12 }}>
                    {visibleTickets.map((ticket, index) => (
                      <React.Fragment key={ticket.id}>
                        {renderTicketItem(ticket, index)}
                      </React.Fragment>
                    ))}
                  </View>
                )}
              </View>
            ) : null}
          </View>
        </ScrollView>
      ) : layoutWide ? (
        <ScrollView
          contentContainerStyle={styles.listContent}
          refreshControl={listRefreshControl}
          keyboardShouldPersistTaps="handled"
        >
          <ListHeaderComponent />
          {visibleTickets.length === 0 ? (
            filterEmptyComponent
          ) : (
            <View style={styles.workspaceRow}>
              <View style={styles.boardColumns}>
                {boardColumns.map((column, columnIndex) => (
                  <View key={`board-column-${columnIndex}`} style={styles.boardColumn}>
                    {column.map(({ ticket, index }) => (
                      <React.Fragment key={ticket.id}>
                        {renderTicketItem(ticket, index)}
                      </React.Fragment>
                    ))}
                  </View>
                ))}
              </View>
              {showSideRail ? renderSideRail() : null}
            </View>
          )}
        </ScrollView>
      ) : (
        <ScrollView
          contentContainerStyle={styles.listContent}
          refreshControl={listRefreshControl}
          keyboardShouldPersistTaps="handled"
        >
          <ListHeaderComponent />
          {visibleTickets.length === 0 ? (
            filterEmptyComponent
          ) : (
            <View style={{ gap: 12 }}>
              {visibleTickets.map((item, index) => (
                <React.Fragment key={item.id}>
                  {renderTicketItem(item, index)}
                </React.Fragment>
              ))}
            </View>
          )}
        </ScrollView>
      )}
    </ShellContentFrame>
  );
}
